/**
 * Small presentational atoms shared by both access boards — the source chip,
 * the role badge, the expiry chip and the request-status pill. Each keeps the
 * "never colour-only" contract: glyph + label always accompany the tint.
 */

import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { Ban, Check, Clock, Hourglass, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SOURCE_META, remaining, relationKey, roleKey } from './accessMeta'
import type { AccessSource, RequestStatus } from './types'

/** Where a grant comes from — glyph + tint + label (e.g. "via role"). */
export function SourceChip({ source, className }: { source: AccessSource; className?: string }) {
  const { t } = useTranslation()
  const meta = SOURCE_META[source]
  const Icon = meta.icon
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px] font-semibold',
        meta.chip,
        className,
      )}
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
      {t(meta.labelKey)}
    </span>
  )
}

/** The effective role a holder has on a resource (viewer / editor / owner / …). */
export function RoleBadge({ role, className }: { role: string; className?: string }) {
  const { t } = useTranslation()
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border border-neutral-200 bg-white px-2 py-0.5 text-[12px] font-semibold text-neutral-700',
        className,
      )}
    >
      {t(roleKey(role), { defaultValue: role })}
    </span>
  )
}

/** A relation label used as an edge caption in the explanation chain. */
export function relationLabel(t: TFunction, relation: string): string {
  return t(relationKey(relation), { defaultValue: relation })
}

/**
 * Remaining-time chip. Amber (citation) with an hourglass when ≤2h out or
 * lapsed, calm neutral with a clock otherwise — colour + glyph + text, so the
 * urgency reads without relying on hue.
 */
export function ExpiryChip({
  expiresAt,
  now,
  className,
}: {
  expiresAt: string
  now?: number
  className?: string
}) {
  const { t } = useTranslation()
  const r = remaining(expiresAt, now)
  const Icon = r.soon ? Hourglass : Clock
  const label = r.expired
    ? t('access.expiry.expired')
    : t(`access.expiry.${r.unit}`, { count: r.value })
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px] font-semibold',
        r.soon ? 'bg-citation-50 text-citation-700' : 'bg-neutral-100 text-neutral-600',
        className,
      )}
      title={new Date(expiresAt).toLocaleString()}
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
      {label}
    </span>
  )
}

const STATUS_STYLE: Record<RequestStatus, { chip: string; icon: typeof Check }> = {
  pending: { chip: 'bg-citation-50 text-citation-700', icon: Hourglass },
  approved: { chip: 'bg-tone-ok-tint text-tone-ok', icon: Check },
  denied: { chip: 'bg-error-50 text-error-700', icon: X },
  expired: { chip: 'bg-neutral-100 text-neutral-500', icon: Clock },
  cancelled: { chip: 'bg-neutral-100 text-neutral-500', icon: Ban },
}

/** Outgoing-request status — glyph + tint + word, never colour alone. */
export function StatusPill({ status, className }: { status: RequestStatus; className?: string }) {
  const { t } = useTranslation()
  const s = STATUS_STYLE[status]
  const Icon = s.icon
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px] font-semibold',
        s.chip,
        className,
      )}
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
      {t(`access.status.${status}`)}
    </span>
  )
}
